// OperationFormModal.jsx
import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const OperationFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  disabled
}) => {

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={onClose}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        {/* Modal content */}
         <div className="fixed inset-0 overflow-y-auto">
           <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
             <Transition.Child
               as={Fragment}
               enter="ease-out duration-300"
               enterFrom="opacity-0 scale-95 translate-y-4"
               enterTo="opacity-100 scale-100 translate-y-0"
               leave="ease-in duration-200"
               leaveFrom="opacity-100 scale-100 translate-y-0"
               leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">

              <div className="flex items-center justify-between border-b border-gray-200 bg-[#90d824] px-6 py-4">
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  {formData.id ? 'Edit Operation Schedule' : 'New Operation Schedule'}
                </Dialog.Title>                
                </div>

                <form onSubmit={onSubmit} className="px-6 py-5 mt-4 space-y-4">
                  {/* description */}
                  <div>
                    <label className="block text-sm font-medium">Description</label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded py-2 px-3"
                    />
                  </div>
                                    
                  {/* Dates & Times */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium">Start Date</label>
                      <input
                        type="date"
                        name="schedule_from"
                        value={formData.schedule_from || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium">End Date</label>
                      <input
                        type="date"
                        name="schedule_to"
                        value={formData.schedule_to || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium">Start Time</label>
                      <input
                        type="time"
                        name="start_time"
                        value={formData.start_time || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium">End Time</label>
                      <input
                        type="time"
                        name="end_time"
                        value={formData.end_time || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                      />
                    </div>
                  </div>

                  {/* Status / User / Log Date */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium">Status</label>
                      <select
                        name="status"
                        value={formData.status || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        required
                      >
                        <option value="">Select…</option>
                        <option>Scheduled</option>
                        <option>In Progress</option>
                        <option>Completed</option>
                        <option>Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium">Date</label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium">Time Log</label>
                      <input
                        type="time"
                        name="time_log"
                        value={formData.time_log || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border rounded py-2 px-3"
                        required
                      />
                    </div>
                  </div>

                  {/* Technician */}
                  <div>
                    <label className="block text-sm font-medium">Technician</label>
                    <select
                      name="tech_id"
                      value={formData.tech_id || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded py-2 px-3"
                    >
                      <option value="">Select Technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.first_name} {tech.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* User Comment */}
                  <div>
                    <label className="block text-sm font-medium">User Comment</label>
                    <textarea
                      name="user_comment"
                      value={formData.user_comment || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border rounded py-2 px-3"
                      rows={3}
                    />
                  </div>

                  {/* Footer buttons */}
                  <div className="mt-6 flex justify-end space-x-2">
                    {/* {isEditing && (
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="inline-flex justify-center rounded-md border border-transparent
                                  bg-red-700 px-4 py-2 mr-8 text-sm font-medium text-white shadow-sm hover:bg-red-400"
                      >
                        Delete
                      </button>
                    )} */}
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md border border-transparent bg-[#90d824] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#679c18]"
                    >
                      {formData.id ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default OperationFormModal;
